import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { modePanelIds, type ModeEntry } from '@/lib/modes';
import { AdminPage, RegistryTable, StatusPill, FeedbackControl } from '@/admin';

type Row = { id: string; tier: 'required' | 'recommended'; sex?: string };

export function ModePanelsPage() {
  const { modeId = '' } = useParams();
  const qc = useQueryClient();
  const mq = useQuery({ queryKey: ['mode', modeId], queryFn: () => adminApi.modes.get(modeId) });
  const pq = useQuery({ queryKey: ['panels'], queryFn: () => adminApi.panels.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', 'panels'], queryFn: () => feedbackApi.summary('panels') });

  const mode = (mq.data?.mode ?? {}) as ModeEntry;
  const rows: Row[] = modePanelIds(mode);
  const panels = pq.data?.panels ?? {};
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'panels'] });

  return (
    <AdminPage
      title={`${mode.display_name || modeId} · Panels`}
      description="The coverage bundles this mode requires and recommends. Open a panel to see its biomarkers."
      backTo={{ to: `/admin/modes/${encodeURIComponent(modeId)}`, label: mode.display_name || modeId }}
    >
      <RegistryTable<Row>
        columns={[
          { key: 'panel', header: 'Panel', render: (r) => {
            const p = panels[r.id];
            return (
              <div>
                <Link to={`/admin/panels/${encodeURIComponent(r.id)}`} className="font-semibold text-accent hover:underline">{p?.label || r.id}</Link>
                <div className="font-mono text-[11px] text-text-muted">{r.id}</div>
              </div>
            );
          } },
          { key: 'tier', header: 'Tier', render: (r) => (
            <span className="flex items-center gap-1">
              <StatusPill on={r.tier === 'required'} label={r.tier} />
              {r.sex ? <span className="text-[11px] text-text-muted">{r.sex} only</span> : null}
            </span>
          ) },
          { key: 'biomarkers', header: 'Biomarkers', className: 'font-mono text-[12px]', render: (r) => panels[r.id]?.biomarker_count ?? (panels[r.id]?.biomarkers?.length ?? '—') },
          { key: 'recency', header: 'Recency', className: 'text-[12px] text-text-secondary', render: (r) => panels[r.id]?.recency_days ? `${panels[r.id]?.recency_days}d` : '—' },
        ]}
        rows={rows}
        getRowKey={(r) => `${r.id}:${r.sex ?? ''}`}
        count={rows.length}
        state={{ loading: mq.isLoading || pq.isLoading, error: mq.isError || pq.isError }}
        emptyText="This mode references no panels."
        rowActions={(r) => (
          <FeedbackControl registry="panels" itemId={r.id} itemLabel={panels[r.id]?.label || r.id} openCount={fb.data?.[r.id]?.open ?? 0} onChanged={refresh} />
        )}
      />
    </AdminPage>
  );
}
